import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Switch,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import apiClient from '../utils/axios';

const Strategy = () => {
  const [strategies, setStrategies] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingStrategy, setEditingStrategy] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    symbol: '',
    strategyType: 'moving_average',
    parameters: {},
  });
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStrategies();
  }, []);

  const fetchStrategies = async () => {
    try {
      const response = await apiClient.get('/strategy');
      setStrategies(response.data);
    } catch (error) {
      console.error('전략 조회 실패:', error);
    }
  };

  const handleOpen = (strategy = null) => {
    if (strategy) {
      setEditingStrategy(strategy);
      setFormData({
        name: strategy.name,
        symbol: strategy.symbol,
        strategyType: strategy.strategyType,
        parameters: strategy.parameters || {},
      });
    } else {
      setEditingStrategy(null);
      setFormData({
        name: '',
        symbol: '',
        strategyType: 'moving_average',
        parameters: {},
      });
    }
    setOpen(true);
    setError('');
  };

  const handleClose = () => {
    setOpen(false);
    setEditingStrategy(null);
    setFormData({
      name: '',
      symbol: '',
      strategyType: 'moving_average',
      parameters: {},
    });
    setError('');
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.symbol) {
      setError('모든 필드를 입력해주세요.');
      return;
    }

    try {
      if (editingStrategy) {
        await apiClient.put(`/strategy/${editingStrategy.id}`, formData);
      } else {
        await apiClient.post('/strategy', formData);
      }
      fetchStrategies();
      handleClose();
    } catch (error) {
      setError(
        error.response?.data?.error || '전략 저장 중 오류가 발생했습니다.'
      );
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('정말 삭제하시겠습니까?')) {
      try {
        await apiClient.delete(`/strategy/${id}`);
        fetchStrategies();
      } catch (error) {
        alert('삭제 중 오류가 발생했습니다.');
      }
    }
  };

  const handleToggle = async (id) => {
    try {
      await apiClient.patch(`/strategy/${id}/toggle`);
      fetchStrategies();
    } catch (error) {
      alert('상태 변경 중 오류가 발생했습니다.');
    }
  };

  return (
    <Container maxWidth="lg">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
        >
          전략 추가
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>전략명</TableCell>
              <TableCell>종목</TableCell>
              <TableCell>전략 유형</TableCell>
              <TableCell>활성화</TableCell>
              <TableCell>작업</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {strategies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  등록된 전략이 없습니다.
                </TableCell>
              </TableRow>
            ) : (
              strategies.map((strategy) => (
                <TableRow key={strategy.id}>
                  <TableCell>{strategy.name}</TableCell>
                  <TableCell>{strategy.symbol}</TableCell>
                  <TableCell>
                    {strategy.strategyType === 'moving_average'
                      ? '이동평균'
                      : strategy.strategyType === 'rsi'
                      ? 'RSI'
                      : '커스텀'}
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={strategy.isActive}
                      onChange={() => handleToggle(strategy.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleOpen(strategy)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(strategy.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingStrategy ? '전략 수정' : '전략 추가'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            fullWidth
            label="전략명"
            margin="normal"
            value={formData.name}
            onChange={(e) =>
              setFormData({ ...formData, name: e.target.value })
            }
          />
          <TextField
            fullWidth
            label="종목 코드"
            margin="normal"
            value={formData.symbol}
            onChange={(e) =>
              setFormData({ ...formData, symbol: e.target.value })
            }
            helperText="예: 005930 (삼성전자)"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>전략 유형</InputLabel>
            <Select
              value={formData.strategyType}
              onChange={(e) =>
                setFormData({ ...formData, strategyType: e.target.value })
              }
            >
              <MenuItem value="moving_average">이동평균</MenuItem>
              <MenuItem value="rsi">RSI</MenuItem>
              <MenuItem value="custom">커스텀</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>취소</Button>
          <Button onClick={handleSubmit} variant="contained">
            저장
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Strategy;

